import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  Button,
  Input,
  Notification,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import {
  Check,
  CircleExclamation,
  Plus,
  TrashCan,
} from '@unified-checkout/design-system/icons/index';

import {
  addEmail,
  getEmails,
} from '@app/dashboard/services/inner_layout/notifications';
import type {
  INotificationProps,
  TMerchantFlags,
  TModalTypes,
  TNotificationFlags,
} from '@app/dashboard/types/innerLayout/notifications';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import {
  ADD_EMAIL_FIELD_INITIAL_STATE,
  FIELDS,
  MODALS_TYPES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_FLAGS,
} from '@app/dashboard/constants/settings/notifications';
import { MAX_NOTIFY_TIME_OUT } from '@app/dashboard/constants/shared';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import LabelCardInfo from '@app/dashboard/components/inner_layout/LabelCardInfo';
import TableInfo from '@app/dashboard/components/inner_layout/TableInfo';
import { notify } from '@app/dashboard/utils/notifier';

import { formatEmails } from '../../../utils.js';

import ToggleCard from '../shared/ToggleCard';
import AddEmail from '../modals/AddEmail';
import DeleteEmail from '../modals/DeleteEmail';

import styles from './MerchantNotifications.module.scss';

const MerchantNotifications = ({
  merchantFlags,
  updatedFlags,
  setUpdatedFlags,
}: INotificationProps) => {
  const { t } = useTranslation();
  const isFetching = useIsFetching();
  const [openAddEmailInput, setOpenAddEmailInput] = useState(false);
  const [fields, setFields] = useState<IField>(
    ADD_EMAIL_FIELD_INITIAL_STATE(t)
  );
  const [isModalOpen, setIsModalOpen] = useState({
    add: false,
    delete: false,
    confirmation: false,
  });
  const [emailToDelete, setEmailToDelete] = useState<string | null>(null);
  const [isTouched, setIsTouched] = useState(false);

  const { data, isError } = useQuery({
    queryKey: [QUERY_KEYS.GET_EMAILS],
    queryFn: getEmails,
    select: (res) => res.data || [],
  });

  const merchantEmails = useMemo(() => {
    return formatEmails(data);
  }, [data]);

  const isToggleChecked = useMemo(() => {
    const mainObject = isTouched ? updatedFlags : merchantFlags;

    return (
      mainObject?.[
        NOTIFICATION_FLAGS.MERCHANT_TRANSACTION_EMAIL_NOTIFICATION as TNotificationFlags
      ] || false
    );
  }, [merchantFlags, updatedFlags, isTouched]);

  const { mutate: sendOtpMutation, isPending: isSendOtpLoading } = useMutation({
    mutationFn: (payload: { email: string; category: string }) =>
      addEmail(payload),
    onSuccess: () => {
      handleOpenModal(MODALS_TYPES.ADD);
    },
    onError: (axiosErr: AxiosError<{ message?: string }>) => {
      let message;

      if (axiosErr.response?.data) {
        const data = axiosErr.response.data;
        if (Array.isArray(data)) {
          message = data[0];
        } else {
          message = data?.message;
        }
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }
      notify.error(message || t('errors.somethingWentWrong'));
    },
  });

  const handleOpenModal = (modal: TModalTypes) => {
    setIsModalOpen((prev) => ({ ...prev, [modal]: true }));
  };

  const openEmailInputHandler = () => {
    setOpenAddEmailInput(true);
  };

  const handleDeleteEmail = (email: string) => {
    setEmailToDelete(email);
    handleOpenModal(MODALS_TYPES.DELETE);
  };

  const inviteEmailHandler = () => {
    sendOtpMutation({
      email: fields[FIELDS.EMAIL].value as string,
      category: NOTIFICATION_CATEGORIES.NOTIFICATION,
    });
  };

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    onFieldChangeHandler(
      fields,
      setFields,
      FIELDS.EMAIL,
      targetValue,
      () => {}
    );
  };

  const handleRemoveField = () => {
    setFields((prev) => ({
      ...prev,
      [FIELDS.EMAIL]: {
        ...prev[FIELDS.EMAIL],
        value: '',
        isValid: false,
        errorMsg: '',
      },
    }));
    setOpenAddEmailInput(false);
  };

  const onToggleChangeHandler = (checked: boolean) => {
    if (!isTouched) return;

    setUpdatedFlags((prev) => ({
      ...(prev as TMerchantFlags),
      [NOTIFICATION_FLAGS.MERCHANT_TRANSACTION_EMAIL_NOTIFICATION]: checked,
    }));
  };

  const handleTouchedToggle = () => {
    if (!isTouched) setIsTouched(true);
  };

  return (
    <LabelCardInfo label={t('innerLayout.notificationsPage.customers.title')}>
      <AddEmail
        isModalOpen={isModalOpen.add}
        setIsModalOpen={(value) =>
          setIsModalOpen((prev) => ({ ...prev, add: value }))
        }
        setOpenAddEmailInput={setOpenAddEmailInput}
        email={fields[FIELDS.EMAIL].value as string}
        setFields={setFields}
      />

      <DeleteEmail
        isModalOpen={isModalOpen.delete}
        setIsModalOpen={(value) =>
          setIsModalOpen((prev) => ({ ...prev, delete: value }))
        }
        emailToDelete={emailToDelete}
      />

      {isFetching ? (
        <SkeletonBase height="20px" />
      ) : (
        <ToggleCard
          onClickHandler={handleTouchedToggle}
          title={t(
            'innerLayout.notificationsPage.customers.labels.transactionEmailNotifications'
          )}
          toggleProps={{
            size: 'sm',
            checked: isToggleChecked,
            onChangeHandler: onToggleChangeHandler,
          }}
        />
      )}

      {isFetching ? (
        <SkeletonLoader noOfRows={5} width="100%" height="14rem" />
      ) : (
        <div className={styles['merchant-notifications__emails']}>
          <TableInfo
            maxHeight="20rem"
            columns={[
              {
                id: 'email',
                name: t('innerLayout.notificationsPage.customers.labels.email'),
                select: (row: string) => row,
                render: (phone: string) => phone,
                width: '90%',
              },
              ...(merchantEmails.length > 1
                ? [
                    {
                      id: 'actions',
                      name: '',
                      select: (row: string) => row,
                      render: (email: string) => (
                        <Button
                          leadingIcon={TrashCan}
                          variant="link"
                          color="secondary"
                          size="sm"
                          onClickHandler={() => {
                            handleDeleteEmail(email);
                          }}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            data={merchantEmails}
            componentErrorText={
              isError
                ? t('errors.somethingWentWrong')
                : t('innerLayout.notificationsPage.noAvailableData')
            }
            isLoading={!!isFetching}
            skeltonProps={{
              noOfRows: 4,
              height: '12rem',
              type: 'transparent_box',
              width: '100%',
            }}
          />

          {!openAddEmailInput ? (
            <Button
              text={t(
                'innerLayout.notificationsPage.customers.labels.newEmail'
              )}
              size="md"
              variant="link"
              leadingIcon={Plus}
              onClickHandler={openEmailInputHandler}
            />
          ) : (
            <div
              className={
                styles['merchant-notifications__emails-add-email-wrapper']
              }
            >
              <div
                className={styles['merchant-notifications__emails-add-email']}
                data-email-validation-status={
                  fields[FIELDS.EMAIL].errorMsg ? 'error' : 'default'
                }
              >
                <Input
                  name={FIELDS.EMAIL}
                  label={t(
                    'innerLayout.notificationsPage.customers.labels.email'
                  )}
                  placeholder={t(
                    'innerLayout.notificationsPage.customers.placeholders.email'
                  )}
                  onChange={handleFieldChange}
                  value={fields[FIELDS.EMAIL].value}
                  error={fields[FIELDS.EMAIL].errorMsg}
                  status={fields[FIELDS.EMAIL].errorMsg ? 'error' : 'default'}
                  preventAutoComplete
                />
                <Button
                  text={t('innerLayout.notificationsPage.actions.invite')}
                  size="md"
                  variant="contained"
                  onClickHandler={inviteEmailHandler}
                  disabled={!fields[FIELDS.EMAIL].isValid}
                  loading={isSendOtpLoading}
                />
              </div>
              <Button
                text={t('form.removeField')}
                size="md"
                variant="link"
                color="secondary"
                leadingIcon={TrashCan}
                onClickHandler={handleRemoveField}
                disabled={isSendOtpLoading}
              />
            </div>
          )}
        </div>
      )}
    </LabelCardInfo>
  );
};

export default MerchantNotifications;
